import type { IntegrationDefinition } from "./IntegrationsCatalogShared";
import { textField, tokenField } from "./IntegrationsCatalogShared";

export const catalogPart2: IntegrationDefinition[] = [
  {
    kind: "billit",
    label: "Billit / Peppol",
    group: "Finance & payments",
    description:
      "Send, receive, and inspect structured European e-invoices through Billit.",
    setup:
      "Create a Billit Access Point API credential and register the company for Peppol.",
    capabilities: [
      { key: "accounting", label: "Invoices" },
      { key: "e_invoicing", label: "Peppol" },
    ],
    fields: [
      textField(
        "apiKey",
        "Billit API key",
        "The API key issued by Billit Access Point.",
        {
          type: "password",
          secret: true,
          required: true,
          placeholder: "Paste API key",
        },
      ),
      textField(
        "partyId",
        "Billit PartyID",
        "The Billit company PartyID used to select the company context.",
        { required: true, placeholder: "PartyID" },
      ),
      textField(
        "baseUrl",
        "Billit API URL",
        "The Billit Access Point endpoint.",

        { type: "url", defaultValue: "https://api.billit.be" },
      ),
      textField(
        "vatNumber",
        "Company VAT number",
        "Used for Peppol receiver checks.",
        { required: true, placeholder: "BE0123456789" },
      ),
    ],
  },
  {
    kind: "paytrail",
    label: "Paytrail",
    group: "Finance & payments",
    description:
      "Connect Finnish online bank, card, mobile, and payment reporting flows.",
    setup:
      "Create Paytrail merchant credentials. Payment creation should remain behind an explicit approval step.",
    capabilities: [{ key: "payments", label: "Payment methods & reports" }],
    fields: [
      textField(
        "merchantId",
        "Paytrail merchant ID",
        "The merchant ID from Paytrail.",
        { required: true, placeholder: "Merchant ID" },
      ),
      textField(
        "apiKey",
        "Paytrail secret",
        "The signing secret from Paytrail.",
        {
          type: "password",
          secret: true,
          required: true,
          placeholder: "Paste secret",
        },
      ),
      textField(
        "baseUrl",
        "Paytrail API URL",
        "Only change this for a test environment.",
        { type: "url", defaultValue: "https://services.paytrail.com" },
      ),
    ],
  },
  {
    kind: "payconiq",
    label: "Payconiq",
    group: "Finance & payments",
    description: "Connect Belgian Payconiq payment creation and status checks.",
    setup: "Request Payconiq merchant API access and paste the API key.",
    capabilities: [{ key: "payments", label: "Payment status" }],
    fields: [
      textField(
        "apiKey",
        "Payconiq API key",
        "The API key issued for the merchant account.",
        {
          type: "password",
          secret: true,
          required: true,
          placeholder: "Paste API key",
        },
      ),
      textField(
        "merchantId",
        "Merchant ID",
        "Optional Payconiq merchant identifier.",
        { placeholder: "Merchant ID" },
      ),
      textField(
        "baseUrl",
        "Payconiq API URL",
        "Only change this for the test environment.",
        { type: "url", defaultValue: "https://api.payconiq.com" },
      ),
    ],
  },
  {
    kind: "bancontact",
    label: "Bancontact",
    group: "Finance & payments",
    description: "Connect Belgian Bancontact payment flows and payment status.",
    setup:
      "Request integrated Bancontact merchant credentials from your payment provider.",
    capabilities: [{ key: "payments", label: "Payment status" }],
    fields: [
      tokenField(
        "Bancontact access token",
        "The merchant or gateway token for Bancontact.",
      ),
      textField(
        "baseUrl",
        "Bancontact API URL",
        "The integrated payment API endpoint supplied by your provider.",
        { type: "url", required: true, placeholder: "https://…" },
      ),
    ],
  },
  {
    kind: "google_business_profile",
    label: "Google Business Profile",
    group: "Local business",
    description:
      "Manage locations, menus, opening hours, posts, and customer reviews.",
    setup:
      "Create a Google OAuth token with Business Profile access and enter the account ID.",
    capabilities: [
      { key: "reviews", label: "Reviews" },
      { key: "social", label: "Location content" },
    ],
    fields: [
      tokenField(
        "Google access token",
        "A Google OAuth token for the managed Business Profile account.",
      ),
      textField(
        "accountId",
        "Business Profile account ID",
        "The Google Business Profile account identifier.",
        { required: true, placeholder: "accounts/…" },
      ),
      textField(
        "locationId",
        "Default location ID",
        "Optional default location for review actions.",
        { placeholder: "locations/…" },
      ),
    ],
  },
  ...(
    ["sd_worx", "acerta", "partena_professional", "securex", "liantis"] as const
  ).map((kind): IntegrationDefinition => ({
    kind,
    label: (
      {
        sd_worx: "SD Worx",
        acerta: "Acerta",
        partena_professional: "Partena Professional",
        securex: "Securex",
        liantis: "Liantis",
      } as Record<typeof kind, string>
    )[kind],
    group: "People & sourcing",
    description:
      "Connect Belgian payroll, employee, and workforce data through an approved provider API.",
    setup:
      "Request partner API access from the payroll provider and use the supplied API base URL and token.",
    capabilities: [{ key: "payroll", label: "Payroll & employees" }],
    fields: [
      tokenField(
        "Provider access token",
        "The credential supplied by the payroll provider.",
      ),
      textField(
        "baseUrl",
        "Provider API URL",
        "The API base URL supplied by the provider.",
        { type: "url", required: true, placeholder: "https://…" },
      ),
    ],
  })),
  {
    kind: "ytj_prh",
    label: "Finnish YTJ / PRH",
    group: "Local business",
    description:
      "Look up Finnish company identifiers, legal forms, registers, and business details.",
    setup:
      "The public YTJ/PRH API is read-only. Add a default Business ID for connection testing.",
    capabilities: [{ key: "business_registry", label: "Company verification" }],
    fields: [
      textField(
        "baseUrl",
        "YTJ / PRH API URL",
        "The Finnish company-data API endpoint.",
        {
          type: "url",
          defaultValue: "https://avoindata.prh.fi/opendata-ytj-api/v3",
        },
      ),
      textField(
        "businessId",
        "Default Business ID",
        "Optional Finnish Business ID used for testing and lookups.",
        { placeholder: "1234567-8" },
      ),
    ],
  },
  {
    kind: "cbe_kbo",
    label: "Belgian CBE / KBO",
    group: "Local business",
    description:
      "Look up Belgian enterprise numbers, establishments, activities, and registered addresses.",
    setup:
      "Use the official CBE/KBO open-data or paid web-service access and enter its API endpoint.",
    capabilities: [{ key: "business_registry", label: "Company verification" }],
    fields: [
      textField(
        "baseUrl",
        "CBE / KBO API URL",
        "The official or approved CBE/KBO service endpoint.",
        { type: "url", required: true, placeholder: "https://…" },
      ),
      textField(
        "enterpriseNumber",
        "Default enterprise number",
        "Optional Belgian enterprise number used for testing.",
        { placeholder: "0123.456.789" },
      ),
      tokenField(
        "CBE / KBO access token",
        "Required when using a protected web-service endpoint.",
      ),
    ],
  },
  {
    kind: "vies",
    label: "EU VIES VAT validation",
    group: "Local business",
    description:
      "Validate Finnish, Belgian, and other EU VAT numbers for business onboarding.",
    setup:
      "Enter the country code and VAT number to validate. VIES is a read-only European Commission service.",
    capabilities: [{ key: "business_registry", label: "VAT validation" }],
    fields: [
      textField("countryCode", "Country code", "Two-letter EU country code.", {
        required: true,
        defaultValue: "FI",
        placeholder: "FI or BE",
      }),
      textField(
        "vatNumber",
        "VAT number",
        "Enter the VAT number without the country prefix.",
        { required: true, placeholder: "12345678" },
      ),
    ],
  },
  {
    kind: "gs1_food_data",
    label: "GS1 Food Data",
    group: "People & sourcing",
    description:
      "Connect product, barcode, ingredient, nutrition, and allergen data for menus and purchasing.",
    setup:
      "Request access to the relevant GS1 food-data service and enter the API endpoint and credential.",
    capabilities: [{ key: "food_data", label: "Product & allergen data" }],
    fields: [
      tokenField(
        "GS1 access token",
        "The credential for the GS1 food-data service.",
      ),
      textField(
        "baseUrl",
        "GS1 Food Data API URL",
        "The API endpoint supplied by GS1.",
        { type: "url", required: true, placeholder: "https://…" },
      ),
    ],
  },
  {
    kind: "kespro",
    label: "Kespro",
    group: "People & sourcing",
    description:
      "Connect Finnish foodservice purchasing, deliveries, products, recipes, and margin data.",
    setup:
      "Request Kespro partner or customer API access and enter the supplied API endpoint.",
    capabilities: [
      { key: "procurement", label: "Procurement & deliveries" },
      { key: "food_data", label: "Products & recipes" },
    ],
    fields: [
      tokenField(
        "Kespro access token",
        "The credential supplied for Kespro digital services.",
      ),
      textField(
        "baseUrl",
        "Kespro API URL",
        "The API endpoint supplied by Kespro.",
        { type: "url", required: true, placeholder: "https://…" },
      ),
    ],
  },
  {
    kind: "procountor",
    label: "Procountor",
    group: "Finance & payments",
    description:
      "Connect Finnish accounting, invoices, and financial material.",
    setup:
      "Request Procountor API access and use a read-only accounting role where possible.",
    capabilities: [
      { key: "accounting", label: "Accounting" },
      { key: "e_invoicing", label: "Invoices" },
    ],
    fields: [
      tokenField(
        "Procountor access token",
        "The API credential issued by Procountor.",
      ),
      textField(
        "baseUrl",
        "Procountor API URL",
        "The API endpoint supplied for your Procountor connection.",
        { type: "url", required: true, placeholder: "https://…" },
      ),
    ],
  },
  {
    kind: "fennoa",
    label: "Fennoa",
    group: "Finance & payments",
    description:
      "Connect Finnish accounting entries, invoices, and employee data.",
    setup: "Request Fennoa API access from your accounting partner.",
    capabilities: [
      { key: "accounting", label: "Accounting" },
      { key: "e_invoicing", label: "Invoices" },
    ],
    fields: [
      tokenField("Fennoa access token", "The API credential issued by Fennoa."),
      textField(
        "baseUrl",
        "Fennoa API URL",
        "The Fennoa API endpoint supplied for your connection.",
        { type: "url", required: true, placeholder: "https://…" },
      ),
    ],
  },
  {
    kind: "mollie",
    label: "Mollie",
    group: "Finance & payments",
    description:
      "Read and manage online payments and supported e-invoicing flows.",
    setup: "Create a Mollie API key with the minimum required permissions.",
    capabilities: [
      { key: "payments", label: "Payments" },
      { key: "e_invoicing", label: "E-invoicing" },
    ],
    fields: [
      tokenField(
        "Mollie API key",
        "Use a test key for development and a live key only in production.",
      ),
      textField("baseUrl", "Mollie API URL", "The Mollie API v2 endpoint.", {
        type: "url",
        defaultValue: "https://api.mollie.com/v2",
      }),
    ],
  },
  {
    kind: "odoo",
    label: "Odoo",
    group: "Finance & payments",
    description:
      "Connect ERP, accounting, inventory, and restaurant order data.",
    setup: "Create a dedicated Odoo integration user and API credential.",
    capabilities: [
      { key: "accounting", label: "Accounting" },
      { key: "e_invoicing", label: "Invoices" },
    ],
    fields: [
      tokenField(
        "Odoo API token",
        "The credential for the dedicated Odoo integration user.",
      ),
      textField(
        "baseUrl",
        "Odoo API URL",
        "Your Odoo JSON-RPC or API endpoint.",
        {
          type: "url",
          required: true,
          placeholder: "https://odoo.example.com",
        },
      ),
      textField("database", "Odoo database", "The Odoo database name.", {
        required: true,
        placeholder: "Database name",
      }),
    ],
  },
  {
    kind: "tripadvisor",
    label: "Tripadvisor",
    group: "Local business",
    description: "Read restaurant reviews and reputation signals.",
    setup: "Request Tripadvisor Content API access and add the location ID.",
    capabilities: [{ key: "reviews", label: "Reviews" }],
    fields: [
      tokenField(
        "Tripadvisor API key",
        "The Content API key issued by Tripadvisor.",
      ),
      textField(
        "locationId",
        "Location ID",
        "The Tripadvisor location identifier.",
        { required: true, placeholder: "Location ID" },
      ),
      textField(
        "baseUrl",
        "Tripadvisor API URL",
        "The Tripadvisor Content API endpoint.",
        {
          type: "url",
          defaultValue: "https://api.content.tripadvisor.com/api/v1",
        },
      ),
    ],
  },
  ...(["tableonline", "resdiary"] as const).map(
    (kind): IntegrationDefinition => ({
      kind,
      label: kind === "tableonline" ? "TableOnline" : "ResDiary",
      group: "Restaurant operations",
      description: "Connect restaurant reservations and availability.",
      setup: "Request partner API access and enter the restaurant identifier.",
      capabilities: [{ key: "reservations", label: "Reservations" }],
      fields: [
        tokenField(
          "Reservation API token",
          "The credential supplied by the reservation provider.",
        ),
        textField(
          "restaurantId",
          "Restaurant ID",
          "The provider restaurant identifier.",
          { required: true, placeholder: "Restaurant ID" },
        ),
        textField(
          "baseUrl",
          "Reservation API URL",
          "The provider API endpoint.",
          { type: "url", required: true, placeholder: "https://…" },
        ),
      ],
    }),
  ),
  ...(["just_eat", "foodora"] as const).map((kind): IntegrationDefinition => ({
    kind,
    label: kind === "just_eat" ? "Just Eat" : "Foodora",
    group: "Restaurant operations",
    description: "Connect marketplace orders and delivery status.",
    setup:
      "Request restaurant partner API access and enter the restaurant identifier.",
    capabilities: [
      { key: "orders", label: "Marketplace orders" },
      { key: "delivery", label: "Delivery status" },
    ],
    fields: [
      tokenField(
        "Marketplace API token",
        "The partner credential supplied by the marketplace.",
      ),
      textField(
        "restaurantId",
        "Restaurant ID",
        "The marketplace restaurant identifier.",
        { required: true, placeholder: "Restaurant ID" },
      ),
      textField(
        "baseUrl",
        "Marketplace API URL",
        "The provider API endpoint.",
        { type: "url", required: true, placeholder: "https://…" },
      ),
    ],
  })),
  {
    kind: "peppol_gateway",
    label: "Peppol gateway",
    group: "Finance & payments",
    description:
      "Connect a Peppol access point for structured European e-invoices.",
    setup:
      "Use a certified access point and request its API credential and endpoint.",
    capabilities: [{ key: "e_invoicing", label: "Peppol e-invoices" }],
    fields: [
      tokenField("Peppol API token", "The access point credential."),
      textField(
        "baseUrl",
        "Peppol API URL",
        "The certified access point API endpoint.",
        { type: "url", required: true, placeholder: "https://…" },
      ),
    ],
  },
  ...(["stripe", "sumup", "mobilepay", "klarna"] as const).map(
    (kind): IntegrationDefinition => ({
      kind,
      label: (
        {
          stripe: "Stripe",
          sumup: "SumUp",
          mobilepay: "MobilePay",
          klarna: "Klarna",
        } as Record<typeof kind, string>
      )[kind],
      group: "Finance & payments",
      description:
        "Connect payment creation, status, and transaction reporting.",
      setup:
        "Create a least-privilege payment API credential. Money-moving actions always require an approval.",
      capabilities: [{ key: "payments", label: "Payments" }],
      fields:
        kind === "stripe"
          ? [
              textField(
                "apiKey",
                "Stripe secret key",
                "Use a restricted Stripe key with only the permissions required.",
                {
                  type: "password",
                  secret: true,
                  required: true,
                  placeholder: "sk_…",
                },
              ),
              textField(
                "baseUrl",
                "Stripe API URL",
                "Stripe API v1 endpoint.",
                { type: "url", defaultValue: "https://api.stripe.com/v1" },
              ),
            ]
          : kind === "klarna"
            ? [
                textField(
                  "apiKey",
                  "Klarna API key",
                  "Use the credential issued for the Klarna region.",
                  {
                    type: "password",
                    secret: true,
                    required: true,
                    placeholder: "Paste API key",
                  },
                ),
                textField(
                  "secret",
                  "Klarna API secret",
                  "The secret paired with the Klarna API key.",
                  {
                    type: "password",
                    secret: true,
                    required: true,
                    placeholder: "Paste API secret",
                  },
                ),
                textField(
                  "baseUrl",
                  "Klarna API URL",
                  "The Klarna API endpoint supplied for your market.",
                  { type: "url", required: true, placeholder: "https://…" },
                ),
              ]
            : [
                tokenField(
                  `${kind === "sumup" ? "SumUp" : "MobilePay"} access token`,
                ),
                textField(
                  "baseUrl",
                  "Payment API URL",
                  "The provider endpoint supplied for your account.",
                  { type: "url", required: true, placeholder: "https://…" },
                ),
              ],
    }),
  ),
  ...(["uber_eats", "deliveroo", "bolt_food", "resq_club"] as const).map(
    (kind): IntegrationDefinition => ({
      kind,
      label: (
        {
          uber_eats: "Uber Eats",
          deliveroo: "Deliveroo",
          bolt_food: "Bolt Food",
          resq_club: "ResQ Club",
        } as Record<typeof kind, string>
      )[kind],
      group: "Restaurant operations",
      description:
        "Connect marketplace orders and delivery status for a restaurant.",
      setup:
        "Request restaurant partner API access and provide the restaurant identifier.",
      capabilities: [
        { key: "orders", label: "Marketplace orders" },
        { key: "delivery", label: "Delivery status" },
      ],
      fields: [
        tokenField(
          "Marketplace API token",
          "The partner credential supplied by the marketplace.",
        ),
        textField(
          "restaurantId",
          "Restaurant ID",
          "The marketplace restaurant identifier.",
          { required: true, placeholder: "Restaurant ID" },
        ),
        textField(
          "baseUrl",
          "Marketplace API URL",
          "The provider endpoint supplied for your account.",
          { type: "url", required: true, placeholder: "https://…" },
        ),
      ],
    }),
  ),
  ...(["opentable", "tablebooker", "resengo"] as const).map(
    (kind): IntegrationDefinition => ({
      kind,
      label: (
        {
          opentable: "OpenTable",
          tablebooker: "Tablebooker",
          resengo: "Resengo",
        } as Record<typeof kind, string>
      )[kind],
      group: "Restaurant operations",
      description: "Connect restaurant reservations and availability.",
      setup:
        "Request partner API access and provide the restaurant identifier.",
      capabilities: [{ key: "reservations", label: "Reservations" }],
      fields: [
        tokenField(
          "Reservation API token",
          "The credential supplied by the reservation provider.",
        ),
        textField(
          "restaurantId",
          "Restaurant ID",
          "The provider restaurant identifier.",
          { required: true, placeholder: "Restaurant ID" },
        ),
        textField(
          "baseUrl",
          "Reservation API URL",
          "The provider endpoint supplied for your account.",
          { type: "url", required: true, placeholder: "https://…" },
        ),
      ],
    }),
  ),
  ...(
    [
      "visma_eaccounting",
      "exact_online",
      "teamleader",
      "clearfacts",
      "doccle",
      "unifiedpost",
    ] as const
  ).map((kind): IntegrationDefinition => ({
    kind,
    label: (
      {
        visma_eaccounting: "Visma eAccounting",
        exact_online: "Exact Online",
        teamleader: "Teamleader",
        clearfacts: "ClearFacts",
        doccle: "Doccle",
        unifiedpost: "Unifiedpost",
      } as Record<typeof kind, string>
    )[kind],
    group: "Finance & payments",
    description:
      "Connect accounting, invoicing, and European e-invoice workflows.",
    setup:
      "Create a least-privilege accounting or e-invoicing API credential and use the provider endpoint for your tenant.",
    capabilities: [
      { key: "accounting", label: "Accounting" },
      { key: "e_invoicing", label: "E-invoicing" },
    ],
    fields: [
      tokenField(
        "Provider access token",
        "The credential supplied by the provider.",
      ),
      textField(
        "baseUrl",
        "Provider API URL",
        "The endpoint supplied for your tenant.",
        { type: "url", required: true, placeholder: "https://…" },
      ),
    ],
  })),
  ...(["palkka_fi", "sympa"] as const).map((kind): IntegrationDefinition => ({
    kind,
    label: (
      { palkka_fi: "Palkka.fi", sympa: "Sympa" } as Record<typeof kind, string>
    )[kind],
    group: "People & sourcing",
    description:
      "Connect payroll and employee data through an approved provider API.",
    setup:
      "Request partner API access and use the supplied endpoint and token.",
    capabilities: [{ key: "payroll", label: "Payroll & employees" }],
    fields: [
      tokenField(
        "Provider access token",
        "The credential supplied by the payroll provider.",
      ),
      textField(
        "baseUrl",
        "Provider API URL",
        "The endpoint supplied by the provider.",
        { type: "url", required: true, placeholder: "https://…" },
      ),
    ],
  })),
  ...(["metro_tukku", "meira_nova"] as const).map(
    (kind): IntegrationDefinition => ({
      kind,
      label: (
        { metro_tukku: "Metro-tukku", meira_nova: "Meira Nova" } as Record<
          typeof kind,
          string
        >
      )[kind],
      group: "People & sourcing",
      description:
        "Connect Finnish foodservice products, purchasing, and deliveries.",
      setup:
        "Request customer or partner API access from the wholesaler and use the supplied endpoint and token.",
      capabilities: [
        { key: "procurement", label: "Procurement" },
        { key: "food_data", label: "Product data" },
      ],
      fields: [
        tokenField(
          "Supplier access token",
          "The credential supplied for the foodservice account.",
        ),
        textField(
          "baseUrl",
          "Supplier API URL",
          "The endpoint supplied by the wholesaler.",
          { type: "url", required: true, placeholder: "https://…" },
        ),
      ],
    }),
  ),
  {
    kind: "ponto",
    label: "Ponto",
    group: "Finance & payments",
    description:
      "Connect Belgian open-banking and accounting data where Ponto access is enabled.",
    setup:
      "Create a Ponto application and use the tenant access token and API endpoint supplied by the provider.",
    capabilities: [{ key: "accounting", label: "Financial data" }],
    fields: [
      tokenField(
        "Ponto access token",
        "The credential supplied for the connected organization.",
      ),
      textField(
        "baseUrl",
        "Ponto API URL",
        "The Ponto endpoint supplied for your tenant.",
        { type: "url", required: true, placeholder: "https://…" },
      ),
    ],
  },
  ...(
    ["fivaldi", "etasku", "yuki", "octopus", "horus", "winbooks"] as const
  ).map((kind): IntegrationDefinition => ({
    kind,
    label: (
      {
        fivaldi: "Visma Fivaldi",
        etasku: "eTasku",
        yuki: "Yuki",
        octopus: "Octopus",
        horus: "Horus",
        winbooks: "WinBooks",
      } as Record<typeof kind, string>
    )[kind],
    group: "Finance & payments",
    description:
      "Connect accounting, expense, and structured e-invoice workflows.",
    setup:
      "Create the provider API credential and use the endpoint supplied for the company or accounting domain.",
    capabilities: [
      { key: "accounting", label: "Accounting" },
      { key: "e_invoicing", label: "E-invoicing" },
    ],
    fields: [
      tokenField(
        "Provider access token",
        "The credential supplied by the provider.",
      ),
      textField(
        "baseUrl",
        "Provider API URL",
        "The endpoint supplied for your tenant.",
        { type: "url", required: true, placeholder: "https://…" },
      ),
    ],
  })),
  ...(["mepco", "palkkaus_fi", "ucm"] as const).map(
    (kind): IntegrationDefinition => ({
      kind,
      label: (
        { mepco: "Mepco", palkkaus_fi: "Palkkaus.fi", ucm: "UCM" } as Record<
          typeof kind,
          string
        >
      )[kind],
      group: "People & sourcing",
      description:
        "Connect payroll and employee data through an approved provider API.",
      setup:
        "Request partner API access and use the supplied endpoint and token.",
      capabilities: [{ key: "payroll", label: "Payroll & employees" }],
      fields: [
        tokenField(
          "Provider access token",
          "The credential supplied by the payroll provider.",
        ),
        textField(
          "baseUrl",
          "Provider API URL",
          "The endpoint supplied by the provider.",
          { type: "url", required: true, placeholder: "https://…" },
        ),
      ],
    }),
  ),
  ...(["worldline", "nets_nexi"] as const).map(
    (kind): IntegrationDefinition => ({
      kind,
      label: (
        { worldline: "Worldline", nets_nexi: "Nets / Nexi" } as Record<
          typeof kind,
          string
        >
      )[kind],
      group: "Finance & payments",
      description:
        "Connect European payment processing and transaction status flows.",
      setup:
        "Request merchant API access and keep payment creation behind an explicit approval.",
      capabilities: [{ key: "payments", label: "Payments" }],
      fields: [
        tokenField(
          "Payment API token",
          "The credential supplied for the merchant account.",
        ),
        textField(
          "baseUrl",
          "Payment API URL",
          "The provider endpoint supplied for your account.",
          { type: "url", required: true, placeholder: "https://…" },
        ),
      ],
    }),
  ),
  {
    kind: "e_invoice_be",
    label: "e-invoice.be",
    group: "Finance & payments",
    description:
      "Send and receive structured Belgian Peppol invoices through a REST API.",
    setup:
      "Create an e-invoice.be API credential and use a test endpoint before enabling production invoice sending.",
    capabilities: [{ key: "e_invoicing", label: "Peppol e-invoices" }],
    fields: [
      tokenField(
        "e-invoice.be API token",
        "The API credential supplied by e-invoice.be.",
      ),
      textField(
        "baseUrl",
        "e-invoice.be API URL",
        "The e-invoice.be REST endpoint.",
        { type: "url", defaultValue: "https://api.e-invoice.be" },
      ),
    ],
  },
];
